import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { DEFAULT_ADS_SEARCH } from "../lib/urlState.js";
import { defaultAdManagementSearch } from "../lib/adManagementState.js";
import { CONTENT_MAX_WIDTH_CLASS } from "../lib/tableLayout.js";
import { rum } from "../perf/rum.js";
import { ensureSession, retrySession, signOut } from "../lib/session.js";
import { useAuthStore } from "../store/authStore.js";
import { useUiStore } from "../store/uiStore.js";
import { Chip } from "./Chip.js";
import { ChangePasswordDialog } from "./ChangePasswordDialog.js";
import { CommandPalette } from "./CommandPalette.js";
import { GlobalFilterBar } from "./GlobalFilterBar.js";
import { NAV_ITEMS, groupNavItems, type NavItem, type RoutePath } from "./navItems.js";
import { accessOfAuthUser, canViewPage } from "../lib/permissions.js";
import { RouteSkeleton } from "./RouteSkeleton.js";
import { ShortcutHelp } from "./ShortcutHelp.js";
import { StateView } from "./StateView.js";
import { Toaster } from "./Toaster.js";

/**
 * 应用外壳 = **console 定稿的视觉语言 + 真实路由/鉴权/性能采集**。
 *
 * 结构（照 console-preview.html / ConsolePage.tsx 的骨架，逐段对应）：
 *   ┌ 全宽 TopBar：品牌标记 · 当前页标题 · 副标题 ────── 右侧：全局品牌/店铺 + 用户/退出 ┐
 *   ├ Sidebar（分区导航 + 图标）          │ 页面内容（各路由自己渲染工具条/表格）       │
 *   └ 左下角：设置入口 ───────────────────────────────────────────────────────────────┘
 *
 * 顶栏右侧的**全局归属栏**（品牌 / 店铺）是 R12 定稿：这两项是"我在看哪个品牌的哪个店"，
 * 必须常驻、跨页一致、选择持久化（见 `GlobalFilterBar.tsx` 与 `store/contextStore.ts`）；
 * 时间范围/语言等**页面上下文**留在各页数据区左上角，两者不混放（ux-patterns §9）。
 *
 * 与原 `AppShell` 的差异（都是「把 console 语言套上去」的必要改动）：
 *   1. TopBar 改成**横跨全宽**（console 的做法），不再是「侧栏顶上放品牌、右侧另起 header」；
 *   2. 导航从「4 条带快捷键角标的平铺项」改成**分区导航**（经营 / 广告 / 数据 / 系统），
 *      每项带线性图标，选中态 = `--bg-selected` + `--accent` 图标；
 *   3. 侧栏底部加「设置入口 + 数据上下文」块，与 console 的左下角设置一致。
 *
 * 刻意保留的东西（这些不是装饰，是能力）：
 *   - `/v1/auth/me` 为准的路由守卫、401 回跳、error 态重试（StateView）；
 *   - G+1..5 键盘导航、⌘K 命令面板、Shift+? 帮助、RUM 路由耗时采集；
 *   - 密度切换（信息密度是本项目的核心诉求，不为了「像 Polaris」把密度控制砍掉）。
 *
 * ## 2026-10-04 第三轮（用户第 1、4 条）落在这里
 *
 *  · **第 1 条「页面收窄，不要自动扩展」**：内容区包一层
 *    `mx-auto w-full max-w-[var(--content-max-width)]`（常量在 `lib/tableLayout.ts`）。
 *    TopBar 仍横跨全宽，只有内容收窄；超宽屏上是两侧留白，而不是把表格列拉散
 *    （表格自身仍保留横向滚动）。上限对**所有**页面生效——用户的口径是
 *    「宽度刚好的广告管理的宽度即可」，全站一个内容宽度才不会出现「这个页面能拉到 3000px」。
 *  · **第 4 条「设置改成大弹窗」**：左下角入口从「指向设置路由的 Link」改成
 *    `<button>`（打开 `settingsOpen`，**不导航**），弹窗由 `SettingsDialog` 懒加载渲染，
 *    关闭只是卸载这一层 → 当前页面的筛选 / 展开 / 滚动位置全部保留。
 */

const FALLBACK_NAV: NavItem = NAV_ITEMS[0] ?? {
  to: "/overview",
  label: "经营概览",
  hint: "",
  section: "经营",
  badge: "1",
  icon: "",
};

/** 左下角设置入口的图标（与 console 的 NAV_ICONS.设置 同一几何）。 */
const SETTINGS_ICON =
  "M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM10 2.5v2m0 11v2M3.7 6.2l1.7 1M14.6 12.8l1.7 1M3.7 13.8l1.7-1M14.6 7.2l1.7-1";

/**
 * 设置大弹窗（用户第三轮第 4 条）**懒加载**：设置内容不是首屏需要的东西，
 * 但「点了就开」的手感不能丢 —— 见 `preloadSettings()`（hover / focus 即预取）。
 */
const SettingsDialogLazy = lazy(() =>
  import("./SettingsDialog.js").then((mod) => ({ default: mod.SettingsDialog })),
);

let settingsChunkRequested = false;

/** 预取设置弹窗的 chunk（幂等；入口 hover / focus 时调用）。 */
function preloadSettings(): void {
  if (settingsChunkRequested) return;
  settingsChunkRequested = true;
  void import("./SettingsDialog.js");
}

/** console 的图标画法：20×20 线性、currentColor、1.6 描边。 */
function NavIcon({ path }: { readonly path: string }) {
  return (
    <svg viewBox="0 0 20 20" className="size-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden>
      <path d={path} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function AppShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const helpOpen = useUiStore((state) => state.helpOpen);
  const setCommandOpen = useUiStore((state) => state.setCommandOpen);
  const setHelpOpen = useUiStore((state) => state.setHelpOpen);
  const settingsOpen = useUiStore((state) => state.settingsOpen);
  const setSettingsOpen = useUiStore((state) => state.setSettingsOpen);
  const sessionStatus = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const sessionError = useAuthStore((state) => state.error);
  const pendingG = useRef<number | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  /**
   * ⭐ 自助改密弹窗；`forced` 由**服务端 `mustChangePassword`** 驱动
   * （后端闸门此刻会拒掉改密与 `/auth/me` 之外的一切请求 ⇒ 不可关闭）。
   * ⛔ **不看 `featureAccess`**（改自己密码是基本安全能力，与登录/登出同级）。
   */
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const forcePasswordChange = user?.mustChangePassword === true;

  const isLogin = pathname === "/login";
  const active = NAV_ITEMS.find((item) => item.to === pathname) ?? FALLBACK_NAV;

  /**
   * C1 Phase 3：**导航按功能权限过滤**（`canView` 为 false 的页面不出现）。
   *
   *  · 映射只有一处（`lib/permissions.ts` 的 `PAGE_FEATURES`）——这里只问「这条导航指向的
   *    页面能不能看」，不再维护第二份 feature↔页面表；
   *  · 会话尚未加载（`user === null`）时**不预过滤**：此刻还没有权限数据，
   *    提前过滤会让导航闪空；真正的闸门是路由守卫 + 服务端（前端隐藏只是体验）；
   *  · 会话加载后（含受限用户）按 `canViewPage` 过滤；超管由契约直接旁路全可见。
   */
  const access = useMemo(() => accessOfAuthUser(user), [user]);
  const navItems = useMemo(
    () => (access === null ? NAV_ITEMS : NAV_ITEMS.filter((item) => canViewPage(access, item.to))),
    [access],
  );

  // 路由切换耗时：点击时 markNavStart()，这里在新路由渲染后收尾
  useEffect(() => {
    rum.markRouteRendered(pathname);
  }, [pathname]);

  // 会话校验：进入任何受保护路由都以 /v1/auth/me 为准（已认证时是空操作，有请求去重）
  useEffect(() => {
    if (isLogin) return;
    void ensureSession();
  }, [isLogin, pathname]);

  // 未登录 → SPA 跳登录（带原页面地址，登录后回跳）
  useEffect(() => {
    if (isLogin || sessionStatus !== "unauthenticated") return;
    const redirect = `${pathname}${window.location.search}`;
    void navigate({ to: "/login", search: { redirect }, replace: true });
  }, [isLogin, navigate, pathname, sessionStatus]);

  const goTo = (to: RoutePath): void => {
    rum.markNavStart();
    if (to === "/") void navigate({ to, search: DEFAULT_ADS_SEARCH });
    else if (to === "/ad-management") void navigate({ to, search: defaultAdManagementSearch() });
    else void navigate({ to });
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

      const meta = event.metaKey || event.ctrlKey;
      if (meta && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(!useUiStore.getState().commandOpen);
        return;
      }
      if (event.key === "?" && !typing) {
        event.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (event.key === "Escape" && !typing) {
        setCommandOpen(false);
        setHelpOpen(false);
        // 正常路径下设置弹窗由 Modal 自己的 Esc 处理（capture 阶段就 stopPropagation）；
        // 这里兜底，保证「Esc 一定关得掉」。
        setSettingsOpen(false);
        return;
      }
      if (typing || meta) return;
      // 设置弹窗是模态的：它开着时不响应 g 前缀导航（否则会把用户"导航走"而弹窗还在）。
      if (useUiStore.getState().settingsOpen) return;

      // g 前缀导航：g 然后 1–5
      if (event.key.toLowerCase() === "g") {
        pendingG.current = Date.now();
        return;
      }
      if (pendingG.current !== null && Date.now() - pendingG.current < 1200) {
        const item = navItems.find((candidate) => candidate.badge === event.key);
        pendingG.current = null;
        if (item !== undefined) {
          event.preventDefault();
          goTo(item.to);
        }
      } else {
        pendingG.current = null;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // goTo 只依赖 navigate（稳定），无需进依赖数组；`navItems` 已按权限过滤且 useMemo 稳定。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCommandOpen, setHelpOpen, setSettingsOpen, navItems]);

  const handleSignOut = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
    await navigate({ to: "/login", search: { redirect: "/" }, replace: true });
  };

  // ---- 守卫分支（所有 hook 已在上方无条件调用）----

  if (isLogin) return <Outlet />;

  if (sessionStatus === "unknown" || sessionStatus === "checking") {
    return (
      <div className="h-screen w-full overflow-hidden bg-canvas">
        <RouteSkeleton variant="cards" />
      </div>
    );
  }

  if (sessionStatus === "unauthenticated") {
    // 正在跳转登录页；保持骨架避免闪现受保护内容
    return (
      <div className="h-screen w-full overflow-hidden bg-canvas">
        <RouteSkeleton variant="cards" />
      </div>
    );
  }

  if (sessionStatus === "error") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-canvas">
        <StateView
          kind="error"
          title="无法确认登录状态"
          description={sessionError ?? "无法连接 /v1/auth/me；这不等于未登录，请重试"}
          actionLabel="重试"
          onAction={() => void retrySession()}
        />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-canvas text-ink">
      {/* ---------------------------------------------------------------- TopBar
          console 把标题放左、上下文放右，且工具栏横跨全宽。 */}
      <header className="flex h-[var(--topbar-height)] shrink-0 items-center gap-3 border-b border-line-subtle bg-surface px-4">
        <span
          className="grid h-6 w-6 shrink-0 place-items-center text-2xs font-bold text-accent-ink"
          style={{ background: "var(--accent)", borderRadius: "var(--radius-sm)" }}
        >
          AMS
        </span>
        <span className="h-5 w-px shrink-0" style={{ background: "var(--border-subtle)" }} />
        <h1 className="shrink-0 text-lg font-semibold tracking-tight text-ink">{active.label}</h1>
        <span className="hidden truncate text-meta text-ink-3 md:inline">{active.hint}</span>

        <div className="ml-auto flex items-center gap-2">
          {/* 全局归属（品牌 / 店铺）常驻右上角（R12；ux-patterns §9 第 4 条） */}
          <GlobalFilterBar />

          {user === null ? null : (
            <span className="flex min-w-0 items-center gap-1.5 text-meta text-ink-2">
              <span className="max-w-40 truncate" title={user.username}>
                {user.username}
              </span>
              <Chip tone="neutral">{user.role}</Chip>
            </span>
          )}

          {/* ⭐ 自助改密（**基本安全能力**，⛔ 不挂任何功能点、不受权限阻断）。 */}
          <button
            type="button"
            data-change-password-open="true"
            onClick={() => setPasswordDialogOpen(true)}
            className="border border-line px-2 text-meta text-ink-2 transition-colors dur-fast hover:bg-hover hover:text-ink"
            style={{ height: "var(--control-height)", borderRadius: "var(--radius-control)" }}
          >
            修改密码
          </button>

          <button
            type="button"
            onClick={() => void handleSignOut()}
            disabled={signingOut}
            className="border border-line px-2 text-meta text-ink-2 transition-colors dur-fast hover:bg-hover hover:text-ink disabled:opacity-50"
            style={{ height: "var(--control-height)", borderRadius: "var(--radius-control)" }}
          >
            {signingOut ? "退出中…" : "退出登录"}
          </button>
        </div>
      </header>

      {passwordDialogOpen || forcePasswordChange ? (
        <ChangePasswordDialog
          forced={forcePasswordChange}
          onClose={() => setPasswordDialogOpen(false)}
          onSuccess={() => setPasswordDialogOpen(false)}
        />
      ) : null}

      <div className="flex min-h-0 flex-1">
        {/* ------------------------------------------------------------- Sidebar
            console 的分区导航：分组标题 + 图标 + 选中底色；底部常驻设置与上下文。 */}
        <nav
          aria-label="主导航"
          className="flex w-[var(--sidebar-width)] shrink-0 flex-col gap-4 overflow-y-auto border-r border-line-subtle px-3 py-3"
          style={{ background: "var(--bg-surface)" }}
        >
          {groupNavItems(navItems).map((group) => (
            <div key={group.section} className="flex flex-col gap-0.5">
              <span className="px-2 pb-1 text-label font-semibold uppercase text-ink-3">{group.section}</span>
              {group.items.map((item) => {
                const isActive = item.to === pathname;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => rum.markNavStart()}
                    aria-current={isActive ? "page" : undefined}
                    className={`flex items-center gap-2 px-2 text-left text-table transition-colors dur-fast ${
                      isActive ? "font-medium text-ink" : "text-ink-2 hover:bg-hover hover:text-ink"
                    }`}
                    style={{
                      height: 30,
                      borderRadius: "var(--radius-control)",
                      background: isActive ? "var(--bg-selected)" : undefined,
                    }}
                  >
                    <span style={{ color: isActive ? "var(--accent)" : "var(--fg-tertiary)" }}>
                      <NavIcon path={item.icon} />
                    </span>
                    <span className="truncate">{item.label}</span>
                    <kbd className="ml-auto shrink-0 rounded-xs border border-line px-1 text-2xs text-ink-3">
                    </kbd>
                  </Link>
                );
              })}
            </div>
          ))}

          <div className="mt-auto flex flex-col gap-2 border-t border-line-subtle pt-2">
            {/*
              用户第三轮第 4 条：设置 = **大弹窗**，不再是一个路由跳转。
              所以这里必须是 <button>：点它不导航，只打开弹窗，
              当前页面的筛选/展开/滚动状态因此一个都不丢。
              hover / focus 时预取弹窗 chunk（点了就开，首屏不付这份代码）。
            */}
            <button
              type="button"
              onClick={() => {
                preloadSettings();
                setSettingsOpen(true);
              }}
              onPointerEnter={preloadSettings}
              onFocus={preloadSettings}
              aria-haspopup="dialog"
              aria-expanded={settingsOpen}
              className="flex items-center gap-2 px-2 text-left text-table text-ink-2 transition-colors dur-fast hover:bg-hover hover:text-ink"
              style={{ height: 30, borderRadius: "var(--radius-control)" }}
            >
              <span style={{ color: "var(--fg-tertiary)" }}>
                <NavIcon path={SETTINGS_ICON} />
              </span>
              设置
            </button>
          </div>
        </nav>

        {/*
          用户第三轮第 1 条：内容区**收窄**，不再随视口无限铺满。
          `mx-auto` + 上限在 `lib/tableLayout.ts` 里定义（值 = --content-max-width，
          与设置大弹窗共用同一个口径），超出部分留白。
          这里刻意只包内容区（TopBar 仍横跨全宽，与 console/ERP 的惯例一致），
          并且窄屏仍然自适应（w-full + 表格自身的横向滚动）。
        */}
        <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <div className={`h-full min-h-0 ${CONTENT_MAX_WIDTH_CLASS}`}>
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette />
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      {/* 设置大弹窗：只有打开时才挂载（懒加载 chunk），关闭 = 卸载这一层，不导航。 */}
      {settingsOpen ? (
        <Suspense fallback={null}>
          <SettingsDialogLazy open onClose={() => setSettingsOpen(false)} />
        </Suspense>
      ) : null}
      <Toaster />
    </div>
  );
}
